import { stripMarkdown } from '../utils/stripMarkdown';

// Sentence-ending pattern: period/exclamation/question followed by space or end,
// but not after common abbreviations (e.g., Dr., Mr., etc.)
// NOTE: deliberately NOT global — feedChunk() calls .exec() on a freshly sliced buffer
// each time and relies on the stateless behaviour. The chunker below builds its own
// global copy for scanning.
export const SENTENCE_RE = /(?<![A-Z])[.!?](?:\s|$)/;

const SENTENCE_RE_G = new RegExp(SENTENCE_RE.source, 'g');

/** Piper chunking: keep requests small so first audio starts quickly. */
export const TTS_FIRST_CHUNK_SOFT = 380;
export const TTS_CHUNK_TARGET = 560;
export const TTS_CHUNK_HARD = 960;

/* ── Text chunking ── */
export function splitTextIntoTtsChunks(raw: string): string[] {
  const clean = stripMarkdown(raw).replace(/\r\n/g, '\n').trim();
  if (!clean) return [];
  if (clean.length <= TTS_CHUNK_TARGET) return [clean];

  const chunks: string[] = [];
  let rest = clean;
  let isFirst = true;

  while (rest.length > 0) {
    const softLimit = isFirst ? TTS_FIRST_CHUNK_SOFT : TTS_CHUNK_TARGET;
    const hardLimit = isFirst ? Math.min(520, TTS_CHUNK_HARD) : TTS_CHUNK_HARD;
    isFirst = false;

    if (rest.length <= softLimit) { chunks.push(rest); break; }

    const searchEnd = Math.min(rest.length, hardLimit);
    const slice = rest.slice(0, searchEnd);
    let cut = -1;

    const pp = slice.lastIndexOf('\n\n');
    if (pp >= 96) cut = pp;
    if (cut < 0) { const nl = slice.lastIndexOf('\n'); if (nl >= 64) cut = nl; }
    if (cut < 0) {
      let best = -1;
      // matchAll over a global copy: a non-global RegExp.exec() ignores lastIndex and
      // returns the same first match forever (#2205 — froze the page main thread).
      for (const m of slice.matchAll(SENTENCE_RE_G)) {
        const end = (m.index ?? 0) + m[0].length;
        if (end <= softLimit + 80 && end > best) best = end;
      }
      if (best >= 32) cut = best;
    }
    if (cut < 0) { const sp = slice.lastIndexOf(' ', softLimit); if (sp >= 24) cut = sp; }
    if (cut < 0) cut = searchEnd;

    const piece = rest.slice(0, cut).trim();
    if (!piece) {
      const forced = rest.slice(0, hardLimit).trim();
      if (forced) chunks.push(forced);
      rest = rest.slice(hardLimit).trim();
      continue;
    }
    chunks.push(piece);
    rest = rest.slice(cut).trim();
  }
  return chunks;
}

